import { z } from 'zod';
/**
 * What happened to a tenant's events after they arrived — for the TENANT.
 *
 * `/platform/pipeline-health` answers this for the platform, across every
 * tenant, and a tenant administrator cannot reach it. So a customer whose
 * events stop becoming incidents has had exactly one symptom available to
 * them: an absence. Nothing on any screen they can open says an event arrived
 * and was refused.
 *
 * The design turns on a distinction the processing pipeline already makes, and
 * getting it wrong would leak:
 *
 *  - **`UNRESOLVED_REFERENCE` is a DELIBERATE refusal.** The engine looked at
 *    the event, could not resolve something it names, and said so in wording
 *    written for a reader — "geofence 'Depot North' could not be resolved for
 *    this tenant". The missing thing is the tenant's to create. These are shown
 *    with their reasons.
 *  - **`FAILED` is an unhandled exception**, stored as `(err as Error).message`
 *    straight from the catch block. That string is written for us: it can carry
 *    an internal id, a constraint name, a stack fragment, or a fact about a row
 *    this tenant has no business seeing. It is reported as a COUNT with no text
 *    at all, and named as the platform's responsibility.
 *
 * The count is still shown, because a tenant whose events are failing deserves
 * to know that even when the reason is not theirs to read — silence would be
 * the same defect this endpoint exists to fix, one level down.
 */
/** How many distinct refusal reasons are listed before the list is truncated. */
export const EVENT_FAILURE_REASON_LIMIT = 8;
export const eventRefusalSchema = z.object({
    /**
     * The engine's own wording, verbatim. Never fingerprinted: two refusals
     * differing by the name they could not resolve are two different things to
     * go and fix, and collapsing them would hide the second.
     */
    reason: z.string(),
    count: z.number().int(),
    /** Most recent occurrence, so a reader can tell a live problem from a fixed one. */
    lastSeenAt: z.string(),
});
/**
 * The stage BEFORE the engine: a provider payload becoming a canonical event.
 *
 * ⚠️ **INSTRUMENTED BY NOTHING until 20 September 2026.** Everything below this
 * object describes `EventProcessingRecord`, which is keyed on a canonical event
 * and written when one is persisted — so a payload that never BECOMES a
 * canonical event is in none of those figures. A tenant whose provider changed a
 * field, whose connection was switched off, or whose events were piling up
 * behind a stalled queue opened this panel and read zero refusals and zero
 * failures. Measured at the time: 27 refused and 1 failed inbound event on the
 * development database, against a panel reporting nothing at all.
 *
 * ⚠️ **COUNTS ONLY, and that is a narrower rule than the one above rather than
 * the same one.** At this stage neither message can be shown:
 *
 *  - a `FAILED` message is an unhandled exception's, exactly as below;
 *  - an `UNRESOLVED_REFERENCE` message is `vehicle not found for {json}` — a
 *    serialised reference rather than a sentence, and the *fleet page* already
 *    lists those registrations properly, with the occurrence counts and a
 *    control to create the vehicle. Printing the JSON beside it would be the
 *    same fact twice, worse worded, on the less useful screen.
 *
 * So `refused` points somewhere rather than explaining itself, and that pointer
 * is the whole reason it is a count and not a list.
 */
export const inboundEventHealthSchema = z.object({
    /**
     * Payloads OIP could not map at all. Ours in every case — a tenant cannot
     * change what their provider sends into something an adapter understands.
     */
    platformFailures: z.number().int(),
    platformFailureLastSeenAt: z.string().nullable(),
    /**
     * Payloads refused because they named something this tenant does not hold —
     * today always a vehicle. Detail lives on the fleet page, not here.
     */
    refused: z.number().int(),
    /**
     * Payloads accepted and not yet mapped.
     *
     * ⚠️ Distinguishes "we refused it" from "we have not looked yet", which
     * produce the identical symptom — no incident — and mean opposite things. A
     * number here that does not fall is a stalled queue, and before this field
     * existed that state was invisible on every screen a tenant can open.
     */
    pending: z.number().int(),
    /** When the oldest unmapped payload arrived, so a stall reads as one. */
    oldestPendingAt: z.string().nullable(),
});
export const eventProcessingHealthSchema = z.object({
    /**
     * What became of payloads before the engine ever saw them. A separate object
     * because it is a separate STAGE — see the note on the schema.
     *
     * ⚠️ Never added to the figures beside it. A single "N events had a problem"
     * merges a fault that is ours with one that is the tenant's, and the reader
     * cannot tell which fix applies.
     */
    inbound: inboundEventHealthSchema,
    /**
     * Events the engine deliberately refused, most common first. The fix for
     * these is generally the tenant's own — a vehicle, geofence or driver that
     * the provider names and OIP does not hold.
     */
    refusals: z.array(eventRefusalSchema),
    /** Distinct refusal reasons in total, so a truncated list can say so. */
    distinctRefusals: z.number().int(),
    /**
     * Events that failed with an unhandled error. A COUNT ONLY — see the note
     * above on why the message is withheld.
     */
    platformFailures: z.number().int(),
    /** Most recent unhandled failure, so "still happening" is answerable. */
    platformFailureLastSeenAt: z.string().nullable(),
    /**
     * Events still waiting. Distinguishes "the engine refused it" from "the
     * engine has not looked at it yet" — which read identically from the outside
     * (no incident appeared) and mean opposite things.
     */
    pending: z.number().int(),
});
//# sourceMappingURL=event-processing.js.map